import { GameState } from '@snake/shared';

/**
 * Converts server timestamps to this browser's clock.
 *
 * The server sends deadlines (countdowns, effect expiries, spawn protection)
 * as times on its own clock, and the two clocks can be seconds apart. The
 * offset is measured from WELCOME on every connection, so it includes that
 * message's network delay: converted deadlines land late by that delay, the
 * same delay the game state itself arrives with.
 */
export class ServerClock {
  private offset = 0;

  /** Measure the offset from a server timestamp that has just arrived. */
  sync(serverTime: number): void {
    this.offset = Date.now() - serverTime;
  }

  toLocal(serverTime: number): number {
    return serverTime + this.offset;
  }

  /** Convert every timestamp in a game state, in place. */
  localizeGameState(state: GameState): GameState {
    if (state.startTime !== undefined) {
      state.startTime = this.toLocal(state.startTime);
    }
    if (state.endTime !== undefined) {
      state.endTime = this.toLocal(state.endTime);
    }
    if (state.racingFood) {
      state.racingFood.spawnTime = this.toLocal(state.racingFood.spawnTime);
    }
    if (state.suddenDeath) {
      const sd = state.suddenDeath;
      sd.countdownEndsAt = this.toLocal(sd.countdownEndsAt);
      sd.nextBlockAt = this.toLocal(sd.nextBlockAt);
    }
    for (const player of Object.values(state.players)) {
      // 0 means unprotected, not a time.
      if (player.spawnProtectionUntil !== 0) {
        player.spawnProtectionUntil = this.toLocal(player.spawnProtectionUntil);
      }
      if (player.pickup) {
        player.pickup.expiresAt = this.toLocal(player.pickup.expiresAt);
      }
      for (const effect of player.effects) {
        effect.expiryTime = this.toLocal(effect.expiryTime);
      }
      for (const obstacle of player.obstacles) {
        obstacle.placementTime = this.toLocal(obstacle.placementTime);
      }
    }
    return state;
  }
}
